import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import MUITable from "../../../../../../../common/components/mui-table/mui-table";
import _ from "lodash";
import { AlertComponent } from "../../../../../../../common/components";
import { Checkbox } from "@mui/material";
import PaginationComponent from "../../../../../../../common/components/pagination-component/pagination-component";
import { useLogin } from "../../../../../../../common/hooks/useUser";
import { useSetLoading } from "../../../../../../../common/hooks/appearance";
import StatusDialog from "../status-dialog/update-status-dialog";
import { getCustomers } from "../../../../../../../api/customer";
import CustomerFilterOptions from "../../../../../../customer/customer-filter/customer-filter-options";
import { filterOptions } from "../../../../../../customer/customer-filter/customer-filter.constant";
import AddCustomerFilter from "./add-customer-filter";
import { campainAssignToCustomers } from "../../../../../../../api/campaign";
import "./add-customer.scss";
import { AlertType } from "../../../../../../../utils/util-types";
interface SortOption {
  sortDirection: string | null;
  sortBy: string | null;
}
interface AddLeadListProps {
  allCampaignData: any;
  setAlertMessage: (msg: string) => void;
  setAlertType: (type: AlertType) => void;
  setAlertOpen: (open: boolean) => void;
  handleCloseModal: () => void;
  campaignName: any;
}
const AddCustomerList: React.FC<AddLeadListProps> = ({
  allCampaignData,
  setAlertMessage,
  setAlertType,
  setAlertOpen,
  handleCloseModal,
  campaignName,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedCustomers, setSelectedCustomers] = useState<number[]>([]);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [selectedCustomersFilter, setSelectedCustomersFilters] = useState<
    number[]
  >([]);
  const [selectedCustomersFilterOptions, setSelectedCustomersFilterOptions] =
    useState<any[]>(filterOptions);
  const [selectedAllCustomers, setSelectedAllCustomers] = useState<any>(null);
  const [selectedCreateCustomers, setSelectedCreateCustomers] =
    useState<any>(null);
  const [selectedActions, setSelectedActions] = useState<any>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [viewOpen, setViewOpen] = useState<boolean>(false);
  const [sortOption, setSortOption] = useState<SortOption>({
    sortDirection: null,
    sortBy: null,
  });
  const [isShowCreateCustomer, setIsShowCreateCustomer] =
    useState<boolean>(false);
  const [isShowCreateCustomerSection, setIsShowCreateCustomerSection] =
    useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<any>({});
  const [searchText, setSearchText] = useState<string>("");
  const [isResetSearch, setIsResetSearch] = useState(false);
  const searchRef = useRef<{ clear: () => void }>(null);
  const [disableMassActions, setDisableMassActions] = useState<boolean>(true);
  const [selectOpen, setSelectOpen] = useState(false);
  const isSearchTriggered = useRef(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("Planned");
  const [newCustomerId, setNewCustomerId] = useState<number | null>(null);

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      setSearchText(text);
      isSearchTriggered.current = true;
      setCurrentPage(1);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    []
  );

  const resetSearch = () => {
    setSearchText("");
    if (searchRef.current) {
      searchRef.current.clear();
    }
    handleSearch("");
  };

  const userDetails = useLogin();
  const leadOwner =
    userDetails?.userDetails?.firstName +
    " " +
    userDetails?.userDetails?.lastName;

  const fetchCustomers = async (
    page = 1,
    search: string = "",
    filters: any = {}
  ) => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      let params: any = {
        limit: pageSize,
        offset: offset,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      if (sortOption?.sortBy) {
        params = { ...params, ...sortOption };
      }
      if (search) {
        params.searchText = search;
      }
      if (filters?.email) {
        params.email = filters.email;
      }
      if (filters?.firstName) {
        params.firstName = filters.firstName;
      }
      if (filters?.lastName) {
        params.lastName = filters.lastName;
      }
      if (filters?.company) {
        params.company = filters.company;
      }
      if (filters?.leadSource) {
        params.leadSource = filters.leadSource;
      }
      if (filters?.dateOfBirthFrom && filters.dateOfBirthTo) {
        if (filters.dateOfBirthFrom.isSame(filters.dateOfBirthTo)) {
          params.dobStart = filters.dateOfBirthFrom.startOf("day");
          params.dobEnd = filters.dateOfBirthTo.endOf("day");
        } else {
          params.dateOfBirthFrom = filters.dateOfBirthFrom;
          params.dateOfBirthTo = filters.dateOfBirthTo;
        }
      }
      if (filters?.customerOwnerIds?.length > 0) {
        params.customerOwner = filters.customerOwnerIds.map(Number);
      }
      const response = await getCustomers(params, "Customer Profile");
      setData(response?.data.data.customersList);
      setTotalRecords(response?.data.data.totalRecords);
      setTimeout(() => setLoading(false), 1000);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch Customers"
      );
      setAlertType("error");
      setAlertOpen(true);
      setError("Failed to fetch customers list");
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!viewOpen) {
      const pageToUse = isSearchTriggered.current ? 1 : currentPage;
      fetchCustomers(pageToUse, searchText, activeFilters);
      isSearchTriggered.current = false;
    }
  }, [currentPage, searchText, isOpen, sortOption, viewOpen, activeFilters]);
  useEffect(() => {
    if (newCustomerId && data.length) {
      const exists = data.some((lead) => lead.id === newCustomerId);
      if (exists) {
        setSelectedCustomersMap((prev) => {
          const currentSelected = prev[currentPage] || [];
          return {
            ...prev,
            [currentPage]: Array.from(
              new Set([...currentSelected, newCustomerId])
            ),
          };
        });
        setNewCustomerId(null); // Clear state after using
      }
    }
  }, [data, newCustomerId, currentPage]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };
  const [selectedCustomersMap, setSelectedCustomersMap] = useState<{
    [page: number]: number[];
  }>({});
  const isAllSelected =
    Array.isArray(data) &&
    data.length > 0 &&
    selectedCustomersMap[currentPage]?.length === data.length;
  const isIndeterminate =
    selectedCustomersMap[currentPage]?.length > 0 &&
    selectedCustomersMap[currentPage]?.length < data.length;
  const handleSelectAll = () => {
    setSelectedCustomersMap((prev) => {
      const currentPageCustomers = Array.isArray(data)
        ? data.map((item) => item.id)
        : [];
      const selectedCustomersOnPage = prev[currentPage] || [];

      if (selectedCustomersOnPage.length > 0) {
        return { ...prev, [currentPage]: [] };
      }
      return { ...prev, [currentPage]: currentPageCustomers };
    });
  };

  const handleCheckboxChange = (id: number) => {
    setSelectedCustomersMap((prev) => {
      const prevSelected = prev[currentPage] || [];
      const newSelected = prevSelected.includes(id)
        ? prevSelected.filter((leadId) => leadId !== id)
        : [...prevSelected, id];

      return {
        ...prev,
        [currentPage]: newSelected,
      };
    });
  };

  const headers = [
    {
      key: "select",
      label: (
        <Checkbox
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={handleSelectAll}
        />
      ),
    },
    { key: "leadName", label: "Customer Name", isSortable: true },
    { key: "accountName", label: "Company", isSortable: true },
    { key: "email", label: "Email", isSortable: true },
    { key: "phone", label: "Phone", isSortable: true },
    { key: "leadOwner", label: "Customer Owner", isSortable: true },
  ];

  const mapColumnNameToField = (columnName: string): string => {
    const mapping: Record<string, string> = {
      leadName: "first_name",
      accountName: "company",
      email: "primary_email_address",
      phone: "primary_phone_number",
      leadSource: "lead_source",
      leadOwner: "customer_owner",
    };

    return mapping[columnName] || columnName;
  };

  const handleSort = (column: string, sortType: string) => {
    let sortValues: SortOption = { sortBy: null, sortDirection: null };
    if (sortType !== "Unsort") {
      const mappedField = mapColumnNameToField(column);
      sortValues = { sortBy: mappedField, sortDirection: sortType };
    }
    setCurrentPage(1);
    setSortOption(sortValues);
  };

  const fetchFullName = (item: any) => {
    const firstName = item?.first_name?.trim();
    const lastName = item?.last_name?.trim();
    const middleName = item?.middle_name ? item?.middle_name?.trim() : "";

    const fullName =
      firstName && lastName
        ? `${firstName} ${middleName} ${lastName}`
        : firstName
          ? firstName
          : middleName
            ? middleName
            : lastName
              ? lastName
              : "-";
    return fullName;
  };

  const formattedData = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map((item) => {
        const firstName = item.first_name?.trim();
        const lastName = item.last_name?.trim();
        const middleName = item?.middle_name ? item?.middle_name?.trim() : "";
        const fullName =
          firstName && lastName
            ? `${firstName ? `${firstName} ` : ""}${middleName ? `${middleName} ` : ""}${lastName}`
            : firstName || middleName || lastName || "-";

        const leadName = fullName;
        const leadOwnerName = fetchFullName(item?.customerOwner);
        return {
          id: item.id,
          accountName: item.company || "-",
          leadName: leadName,
          email: item.primary_email_address || "-",
          phone: item.primary_phone_number || "-",
          leadSource: item.lead_source || "-",
          leadOwner: leadOwnerName,
        };
      });
    }
    return [];
  }, [data, leadOwner]);

  const checkDisableOfActionScope = () => {
    const disableScope = [
      { "All Customers": true },
      { "Selected Customers": true },
    ];
    if (data?.length >= 1) disableScope[0]["All Customers"] = false;
    if (Object.values(selectedCustomersMap)?.flat(Infinity).length >= 1)
      disableScope[1]["Selected Customers"] = false;
    return disableScope;
  };
  useEffect(() => {
    if (Object.values(selectedCustomersMap)?.flat(Infinity).length >= 1) {
      setDisableMassActions(false);
    } else if (
      Object.values(selectedCustomersMap)?.flat(Infinity).length === 0
    ) {
      setDisableMassActions(true);
    }
  }, [selectedCustomersMap]);

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const dateFilterActive =
      activeFilters.createdTimeStart || activeFilters.createdTimeEnd;
    const runFilterActive =
      activeFilters.lastActivityTimeStart || activeFilters.lastActivityTimeEnd;

    const otherFilters = { ...activeFilters };
    delete otherFilters.createdTimeStart;
    delete otherFilters.createdTimeEnd;
    delete otherFilters.lastActivityTimeStart;
    delete otherFilters.lastActivityTimeEnd;

    const otherActiveFilters = Object.values(otherFilters).filter((val) => {
      if (val === undefined || val === "" || val === null) return false;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === "object") {
        return Object.values(val).some(
          (v) => v !== "" && v !== undefined && v !== null
        );
      }
      return true;
    }).length;
    if (dateFilterActive) {
      count += 1;
    }
    if (runFilterActive) {
      count += 1;
    }

    count += otherActiveFilters;

    return count;
  }, [activeFilters]);
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  const [showImportModal, setShowImportModal] = useState(false);

  const noResultText = useMemo(() => {
    if (loading) {
      return "Loading Customers...";
    }
    if (searchText) {
      return "No customers found matching your search criteria";
    }
    return "No customers found";
  }, [loading, searchText]);

  const handleAssignCustomers = async () => {
    const customerIds = Object.values(selectedCustomersMap).flat();
    const campaignId = allCampaignData;

    try {
      await campainAssignToCustomers(
        {
          customerIds,
          campaignId,
          customerStatusInCampaign: statusFilter,
        },
        "View Campaigns"
      );

      setOpenStatusDialog(false);
      handleCloseModal(); // close modal on success
      setAlertMessage(
        customerIds.length > 1
          ? `${campaignName} successfully added to the Customers`
          : `${campaignName} successfully added to the Customer`
      );
      setAlertType("success");
      setAlertOpen(true);
    } catch (error: any) {
      const errorCodeMap = {
        ALL_CUSTOMERS_ALREADY_ASSOCIATED:
          "Selected customers are already added to this campaign.",
      };

      const rawMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to assign customers to campaign.";

      const alrtMessage =
        errorCodeMap[rawMessage as keyof typeof errorCodeMap] || rawMessage;

      setAlertMessage(alrtMessage);
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  return (
    <div className="leadlist-wrapper add-lead-list-wrapper">
      {/* Breadcrumb Section */}
      <div style={{ display: !viewOpen ? "block" : "none" }} className="leadlist-block-container">
        <div>
          <AddCustomerFilter
            isOpenFilter={isOpenFilter}
            setIsOpenFilter={setIsOpenFilter}
            selectedLeadsFilter={appliedFilterCount}
            selectedAllLeads={selectedAllCustomers}
            setSelectedAllLeads={setSelectedAllCustomers}
            selectedCreateLeads={selectedCreateCustomers}
            setSelectedCreateLeads={setSelectedCreateCustomers}
            selectedActions={selectedActions}
            setSelectedActions={setSelectedActions}
            onSearch={handleSearch}
            searchRef={searchRef}
            isResetSearch={isResetSearch}
            resetSearch={resetSearch}
            disableSelectedLeads={checkDisableOfActionScope()}
            setIsShowCreateLead={setIsShowCreateCustomer}
            isShowCreateLead={isShowCreateCustomer}
            disabled={disableMassActions}
            selectOpen={selectOpen}
            setSelectOpen={setSelectOpen}
            totalLeads={data?.length || 0}
            showImportModal={showImportModal}
            setShowImportModal={setShowImportModal}
            isShowCreateLeadSection={isShowCreateCustomerSection}
            setIsShowCreateLeadSection={setIsShowCreateCustomerSection}
            fetchLeads={() => {
              setIsResetSearch(!isResetSearch);
              setSearchText("");
              fetchCustomers(currentPage);
            }}
            showNextButton={
              Object.values(selectedCustomersMap).flat().length > 0
            }
            setOpenStatusDialog={setOpenStatusDialog}
          />
        </div>
        {/* Table Section */}
        <div className="lead-list-table-filter">
          <div
            className="lead-list-table"
            style={isOpenFilter ? { width: "80%" } : {}}
          >
            <MUITable
              headers={headers}
              data={formattedData.map((item) => ({
                key: item.id,
                select: (
                  <Checkbox
                    checked={
                      selectedCustomersMap[currentPage]?.includes(item.id) ||
                      false
                    }
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => handleCheckboxChange(item.id)}
                  />
                ),
                leadName: item.leadName,
                accountName: item.accountName,
                email: item.email,
                phone: item.phone,
                leadSource: item.leadSource,
                leadOwner: item.leadOwner,
              }))}
              onSort={handleSort}
              noResultsTxt={noResultText}
            />
          </div>
          <div
            style={{ display: isOpenFilter ? "block" : "none" }}
            className="lead-list-filter"
          >
            <CustomerFilterOptions
              isOpenFilter={isOpenFilter}
              setIsOpenFilter={setIsOpenFilter}
              selectedCustomersFilter={selectedCustomersFilter}
              setSelectedCustomersFilters={setSelectedCustomersFilters}
              selectedCustomersFilterOptions={selectedCustomersFilterOptions}
              setAlertMessage={setAlertMessage}
              setAlertType={setAlertType}
              setAlertOpen={setAlertOpen}
              setSelectedCustomersFilterOptions={
                setSelectedCustomersFilterOptions
              }
              onApplyFilters={(filters: any) => {
                setCurrentPage(1);
                setActiveFilters(filters);
              }}
              activeFilters={activeFilters}
            />
          </div>
        </div>
        {totalRecords && totalRecords > 0 ? (
          <div
            className="leads-pagination-container"
            style={{
              pointerEvents: showImportModal ? "none" : "auto",
              opacity: showImportModal ? 0.5 : 1,
            }}
          >
            <PaginationComponent
              totalRecords={totalRecords}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        ) : (
          <></>
        )}
      </div>

      <StatusDialog
        open={openStatusDialog}
        onClose={() => setOpenStatusDialog(false)}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        handleSaveStatus={handleAssignCustomers}
      />
    </div>
  );
};

export default AddCustomerList;
